import { useQuery } from "@apollo/client/react";
import { FormattedMessage } from "react-intl";
import { PersonResultRow } from "@/components/common/PersonResultRow";
import { Button } from "@/components/design/Button";
import { personDisplayName, personDisplayRole } from "@/lib/person-display";
import { MyContactsDocument } from "@/graphql/generated/operations";
import type { PlanPerson } from "../project-plan";

// Wer am Projekt ist. Getrennt von der Fläche, auf der man Menschen SUCHT
// (Match): das eine ist Bestand, das andere Beschaffung, und beides in einem
// Screen hiess, dass man den Bestand erst unter drei Angeboten fand.
//
// Zwei Stufen, die man nicht verwechseln darf:
// - MITGLIEDER haben Zugriff auf das Projekt (project_access in genesis).
// - GEFRAGTE stehen in `data.candidates`. Sie sehen das Projekt nicht, lassen
//   sich aber schon für Aufgaben einplanen.
// Das Abzeichen sagt, welches von beidem gilt - sonst sähe eine Liste, in der
// beide stehen, nach mehr Verbindlichkeit aus, als es gibt.

export function ProjectTeamScreen({
  owner,
  members,
  candidates,
  onRemoveCandidate,
  onGoToMatch,
  readOnly = false,
}: {
  /** Die Eigentümerin, eine eigene Rolle wie im Match (createdBy). */
  owner: PlanPerson | null;
  /** Menschen mit Zugriff auf das Projekt, ohne die Eigentümerin. */
  members: readonly PlanPerson[];
  /** Person-Ids der Gefragten. */
  candidates: readonly string[];
  onRemoveCandidate: (personId: string) => void;
  /** Wechselt in den Match-Reiter und öffnet dort den genannten Weg. */
  onGoToMatch: (path: "known" | "match") => void;
  /** Nur lesen (`Project.actions.canUpdate`): der Bestand ohne Entfernen und ohne die Wege zum Suchen. */
  readOnly?: boolean;
}) {
  // Eine Abfrage für die Gefragten: im Dokument stehen nur Ids, die Zeile will
  // Name, Beruf und Bild. Dieselbe Abfrage wie auf der Kontaktseite.
  const asked = useQuery(MyContactsDocument, {
    variables: { input: { first: 50, where: { id: { in: [...candidates] } } } },
    skip: candidates.length === 0,
  });
  const askedRows = (asked.data?.persons?.nodes ?? []).map((p) => ({
    id: p.id,
    name: personDisplayName(p),
    sub: personDisplayRole(p),
    avatarContent: p.avatar?.content,
    avatarMime: p.avatar?.mimeType,
  }));

  // Leer heißt wie im Match: niemand außer der Eigentümerin. Sie allein ist kein Team.
  const empty = members.length === 0 && askedRows.length === 0;
  const withAccess = owner ? [owner, ...members] : members;

  return (
    <div className="max-w-3xl">
      {withAccess.length > 0 ? (
        <div>
          <div className="smycles-field-label mb-1.5">
            <FormattedMessage id="project.team.members" defaultMessage="Mit Zugriff" />
          </div>
          <div className="space-y-2">
            {withAccess.map((person) => (
              <PersonResultRow
                key={person.id}
                id={`project-team-member-${person.id}`}
                person={person}
                title={person.name}
                sub=""
                embedded
                actions={
                  <span className="smycles-field-meta text-ink-muted">
                    {person.id === owner?.id ? (
                      <FormattedMessage id="match.modal.owner" defaultMessage="Eigentümer:in" />
                    ) : (
                      <FormattedMessage id="project.team.badgeMember" defaultMessage="Im Projekt" />
                    )}
                  </span>
                }
              />
            ))}
          </div>
        </div>
      ) : null}

      {empty && !readOnly ? (
        <div className={withAccess.length > 0 ? "mt-5" : ""}>
          <p id="project-team-empty" className="smycles-field-meta max-w-[52ch] text-ink-muted">
            <FormattedMessage
              id="project.team.empty"
              defaultMessage="Noch niemand sonst dabei. Über Match findest du Menschen aus deiner Community, aus einem früheren Match oder über eine neue Suche."
            />
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button id="project-team-find" variant="secondary" size="compact" onClick={() => onGoToMatch("known")}>
              <FormattedMessage id="project.team.toMatch" defaultMessage="Menschen suchen" />
            </Button>
            <Button id="project-team-match" variant="secondary" size="compact" onClick={() => onGoToMatch("match")}>
              <FormattedMessage id="project.team.toNewMatch" defaultMessage="Ich will matchen" />
            </Button>
          </div>
        </div>
      ) : null}

      {askedRows.length > 0 ? (
        <div className={withAccess.length > 0 ? "mt-5" : ""}>
          <div className="smycles-field-label mb-1.5">
            <FormattedMessage id="project.team.asked" defaultMessage="Gefragt" />
          </div>
          <div className="space-y-2">
            {askedRows.map((person) => (
              <PersonResultRow
                key={person.id}
                id={`project-team-asked-${person.id}`}
                person={{
                  id: person.id,
                  name: person.name,
                  avatarContent: person.avatarContent,
                  avatarMime: person.avatarMime,
                }}
                title={person.name}
                sub={person.sub}
                embedded
                actions={
                  readOnly ? undefined : (
                    <Button
                      id={`project-team-asked-${person.id}-remove`}
                      variant="secondary"
                      size="sm"
                      onClick={() => onRemoveCandidate(person.id)}
                    >
                      <FormattedMessage id="project.candidates.remove" defaultMessage="Entfernen" />
                    </Button>
                  )
                }
              />
            ))}
          </div>
          <p className="smycles-field-meta mt-2 text-ink-muted">
            <FormattedMessage
              id="project.team.askedNote"
              defaultMessage="Gefragte sehen das Projekt noch nicht, lassen sich aber schon für Aufgaben einplanen."
            />
          </p>
        </div>
      ) : null}

      {/* Zwei Wege, nicht einer: wer schon Leute hat, sucht entweder gezielt
          weitere oder lässt ein Match eines zusammenstellen. Beides führt in
          denselben Reiter, aber auf verschiedene Flächen darin. */}
      {!empty && !readOnly ? (
        <div className="mt-5 flex flex-wrap gap-2">
          <Button id="project-team-find" variant="secondary" size="compact" onClick={() => onGoToMatch("known")}>
            <FormattedMessage id="project.team.addMore" defaultMessage="Weitere suchen" />
          </Button>
          <Button id="project-team-match" variant="secondary" size="compact" onClick={() => onGoToMatch("match")}>
            <FormattedMessage id="project.team.toNewMatch" defaultMessage="Ich will matchen" />
          </Button>
        </div>
      ) : null}
    </div>
  );
}
